import axios, { AxiosResponse } from 'axios';
import { isValidWorkyServerResponseWithData } from '../common/WorkyAPIUtils';
import { LOCAL_STORAGE_KEYS, isValidString, setAuthFlow, setSessionStorageValue } from '../common/CommonUtilsMethods';
import {
    ServerResponse,
    UpdateLocalCacheWOAuthInfo,
    WorkySignInResponse,
    setGlobalUserDetails,
} from '../remoteExecution/RemoteExecutionServiceImpl';
import { AUTH_FLOW_TYPE } from './WorkyAuthClientConfigInterfaces';
import type { CompleteCustomSignInRequest, WorkyAuthTokensSignInRequestDto } from './WorkyTokenSignInServiceInterfaces';

const AUTH_SIGNIN_TOKENS_API_PATH: string = 'auth/signin/tokens/';
const backendApiBasePathLocal: string = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL ?? '';

const postWorkyTokenSignIn = async (
    requestBody: WorkyAuthTokensSignInRequestDto
): Promise<AxiosResponse<ServerResponse<WorkySignInResponse, Error>>> => {
    return axios.post<ServerResponse<WorkySignInResponse, Error>>(
        `${backendApiBasePathLocal}${AUTH_SIGNIN_TOKENS_API_PATH}`,
        requestBody,
        {
            headers: { 'Content-Type': 'application/json' },
        }
    );
};

const persistCustomAuthSession = (signInResponse: WorkySignInResponse): void => {
    UpdateLocalCacheWOAuthInfo({
        basicDetails: signInResponse.basicJobberDetails,
        input: signInResponse.tokens,
    });
    setGlobalUserDetails({ userInfo: signInResponse.basicJobberDetails.userDetails });
    setAuthFlow(AUTH_FLOW_TYPE.CUSTOM);

    if (isValidString(signInResponse.tokens.idToken)) {
        setSessionStorageValue({
            key: LOCAL_STORAGE_KEYS.ID_TOKEN,
            value: signInResponse.tokens.idToken,
        });
    }
};

const completeCustomSignIn = async (
    request: CompleteCustomSignInRequest
): Promise<WorkySignInResponse | null> => {
    const requestBody: WorkyAuthTokensSignInRequestDto = {
        accessToken: request.cognitoAccessToken,
        idToken: request.cognitoIdToken,
        refreshToken: request.cognitoRefreshToken,
        expiresInSeconds: request.expiresInSeconds,
        primaryWorkyCorpId: request.primaryWorkyCorpId ?? null,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<WorkySignInResponse, Error>> =
            await postWorkyTokenSignIn(requestBody);

        if (!isValidWorkyServerResponseWithData(apiResponse)) {
            console.error('Token sign-in response missing data', apiResponse?.data);
            return null;
        }

        const signInResponse: WorkySignInResponse = apiResponse.data.data;
        persistCustomAuthSession(signInResponse);
        return signInResponse;
    } catch (error: unknown) {
        console.error('Worky token sign-in failed', error);
        return null;
    }
};

export { completeCustomSignIn, persistCustomAuthSession };
